import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { setUser as setSentryUser } from '@/lib/sentry';

const AUTH_CACHE_KEY = 'feedbackhub_auth_user';

interface User {
  id: string;
  email: string;
  name: string;
  alias: string;
  avatarURL?: string;
  isAdmin: boolean;
  adminRole?: string;
}

interface RegisterResult {
  email?: string;
  requiresVerification?: boolean;
  requiresMigrationDecision?: boolean;
  existingData?: {
    alias: string;
    postsCount: number;
    commentsCount: number;
    votesCount: number;
    memberSince: string;
  };
}

interface MigrationDecisionResult {
  email: string;
  requiresVerification: boolean;
}

interface AuthError {
  message: string;
  code?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: AuthError | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<RegisterResult>;
  submitMigrationDecision: (
    email: string,
    password: string,
    name: string,
    decision: 'accept' | 'decline'
  ) => Promise<MigrationDecisionResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to get cached user from sessionStorage
function getCachedUser(): User | null {
  try {
    const cached = sessionStorage.getItem(AUTH_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

// Helper to cache user to sessionStorage
function setCachedUser(user: User | null): void {
  try {
    if (user) {
      sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
    } else {
      sessionStorage.removeItem(AUTH_CACHE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialize from cache to prevent loading flash on navigation
  const cachedUser = useMemo(() => getCachedUser(), []);
  const [user, setUser] = useState<User | null>(cachedUser);
  const [isLoading, setIsLoading] = useState(!cachedUser);
  const [error, setError] = useState<AuthError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      setError(null);
      const data = await api.auth.me();
      setUser(data.user);
      setCachedUser(data.user);
      // Set Sentry user context. `{ user: null }` is the ordinary
      // not-signed-in response, so reading through it threw a TypeError on
      // every anonymous page load - swallowed by the catch below, but logged as
      // a failure and reported to Sentry.
      setSentryUser(
        data.user ? { id: data.user.id, isAdmin: data.user.isAdmin } : null,
      );
    } catch (err) {
      setUser(null);
      setCachedUser(null);
      // Clear Sentry user context
      setSentryUser(null);
      // Only log error for unexpected failures, not for unauthenticated state
      const isAuthError = err instanceof Error && (
        err.message.includes('401') ||
        err.message.toLowerCase().includes('authentication') ||
        err.message.toLowerCase().includes('unauthorized')
      );
      if (!isAuthError) {
        console.error('Failed to refresh user:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initialize CSRF token early to establish cookie
    api.initializeCsrf();
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      setError(null);
      const data = await api.auth.login({ email, password });
      setUser(data.user);
      setCachedUser(data.user);
      // Set Sentry user context
      setSentryUser({ id: data.user.id, isAdmin: data.user.isAdmin });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError({ message });
      throw err;
    }
  }, []);

  const register = useCallback(async (email: string, password: string, name: string): Promise<RegisterResult> => {
    try {
      setError(null);
      const result = await api.auth.register({ email, password, name });
      // Don't set user - registration requires email verification
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError({ message });
      throw err;
    }
  }, []);

  const submitMigrationDecision = useCallback(async (
    email: string,
    password: string,
    name: string,
    decision: 'accept' | 'decline'
  ): Promise<MigrationDecisionResult> => {
    try {
      setError(null);
      const result = await api.auth.migrationDecision({ email, password, name, decision });
      // Don't set user - registration requires email verification
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Migration decision failed';
      setError({ message });
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setError(null);
      await api.auth.logout();
      setUser(null);
      setCachedUser(null);
      // Clear Sentry user context
      setSentryUser(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Logout failed';
      setError({ message });
      throw err;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        error,
        login,
        register,
        submitMigrationDecision,
        logout,
        refreshUser,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
